import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  Theme,
  CSSObject,
  styled,
  Drawer,
  Typography,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import useResponsive from "../hooks/useResponsive";
import { useAlert } from "../contexts/AlertContext";
import { useProjectContext } from "../contexts/ProjectContext";
import LocalStorageUtil from "../utils/localStorageUtil";
import { useTranslation } from "react-i18next";
import { getAppName } from "../utils/commonMethods";
import { getTenantConfig } from "../tenantConfig";
import LogoutDialog from "../components/LogoutDialog";
interface CommonDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": { borderWidth: 0 },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));
const menuItems = [
  {
    text: "Planning",
    icon: "/assets/calendarMenu.svg",
    path: "/planning",
    subPaths: ["/planning/sub", "/planning/another"],
  },
  {
    text: "Taak",
    icon: "/assets/clockMenu.svg",
    path: "/task",
    subPaths: ["/task/sub", "/task/another"],
  },
  {
    text: "Veiligheidspagina",
    icon: "/assets/safetyMenu.svg",
    path: "/safetyinstructions",
    subPaths: ["/safetyinstructions/sub", "/safetyinstructions/another"],
  },
  {
    text: "Uitloggen",
    // Also exists as src/assets/Logout.svg (imported with ?react by Header).
    // Both copies are needed: the CSS mask below requires a real file URL, so
    // this one must stay in public/ and cannot be an inline SVG component.
    icon: "/assets/Logout.svg",
    path: "/login",
    subPaths: [""],
  },
];
export const closedDrawerWidth = 64;

const CommonDrawer: React.FC<CommonDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();

  const { isMobile, isDesktop } = useResponsive();
  const [selectedPath, setSelectedPath] = useState<string>(() => {
    const savedPath = localStorage.getItem("selectedMenuPath");
    return savedPath || location.pathname;
  });
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [itemHeight] = useState(50); // Default height
  const listItemRef = useRef<HTMLLIElement>(null);

  const { showAlert } = useAlert();
  const { clearProject } = useProjectContext();
  const [isLogout, setIsLogout] = useState<boolean>(false);
  const dismissModal = () => {
    setIsLogout(false);
  }
  useEffect(() => {
    const currentItem = menuItems.find((item) => {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const itemSegments = item.path.split("/").filter(Boolean);
      return (
        itemSegments.every(
          (segment, index) => pathSegments[index] === segment
        ) ||
        (item.subPaths &&
          item.subPaths.some((subPath) => {
            const subPathSegments = subPath.split("/").filter(Boolean);
            return subPathSegments.every(
              (segment, index) => pathSegments[index] === segment
            );
          }))
      );
    });
    if (currentItem) {
      setSelectedPath(currentItem.path);
      localStorage.setItem("selectedMenuPath", currentItem.path);
    }
  }, [location.pathname]);

  const isSelected = (itemPath: string) => {
    return selectedPath.startsWith(itemPath);
  };

  const getStripTop = () => {
    const index = menuItems.findIndex((item) => item.text === hoveredItem);
    return `calc(${theme.spacing(8)} + ${index * itemHeight}px)`;
  };

  const handleItemClick = (item: any) => {
    if (item.text === "Uitloggen") {
      // onClose();
      // handleLogout();
      setIsLogout(true);
    } else {
      setSelectedPath(item.path);
      setHoveredItem(null); // Clear hovered item when selecting
      localStorage.setItem("selectedMenuPath", item.path);
      navigate(item.path);
      onClose();
    }
  };

  const handleLogout = () => {
    showAlert(t("login.Logout_success"), "success");
    clearProject();
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
    LocalStorageUtil.clear();
  };

  const getDrawerContent = () => {
    // Select logo based on env
    const appName = getAppName();
    const { logoDrawerSmall, logoDrawerMain, displayName } = getTenantConfig(appName);
    const hoverColor = theme.palette.drawerHover.main;
    return (
      <>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: "64px",
            padding: "4px",
            bgcolor: theme.palette.greyColors.contrastText,
          }}
        >
          <img
            src={
              open
                ? isMobile
                  ? logoDrawerSmall
                  : logoDrawerMain
                : logoDrawerSmall
            }
            alt={`${displayName} Logo`}
            style={{ height: open ? "100%" : "70%" }}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            height: "100%",
          }}
        >
          <List disablePadding>
            {menuItems.map((item, index) => (
              <ListItem
                key={item.text}
                disablePadding
                ref={index === 0 ? listItemRef : null}
              >
                {((item.text === "Uitloggen" && isMobile) ||
                  item.text !== "Uitloggen") && (
                    <ListItemButton
                      selected={isSelected(item.path)}
                      onClick={() => handleItemClick(item)}
                      onMouseEnter={() => setHoveredItem(item.text)}
                      onMouseLeave={() => setHoveredItem(null)}
                      sx={{
                        backgroundColor: isSelected(item.path)
                          ? theme.palette.primary.dark
                          : "transparent",
                        "&:hover": {
                          backgroundColor: hoverColor,
                        },
                        height: "50px",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: theme.palette.common.white,
                          minWidth: 0,
                          marginRight: theme.spacing(2),
                          justifyContent: "center",
                          ml: "6px"
                        }}
                      >
                        <div
                          style={{
                            width: `${22}px`,
                            height: `${22}px`,
                            backgroundColor: theme.palette.common.white,
                            WebkitMask: `url(${item.icon}) no-repeat center / contain`,
                            mask: `url(${item.icon}) no-repeat center / contain`,
                          }}
                        />
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={item.text}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: theme.palette.common.white,
                              fontSize: "18px",
                              fontFamily: "Nunito Sans SemiBold",
                            },
                          }}
                        />
                      )}
                    </ListItemButton>
                  )}
              </ListItem>
            ))}
          </List>
        </Box>
      </>
    );
  };

  return (
    <>
      {isDesktop ? (
        <StyledDrawer variant={"permanent"} open={open}>
          {getDrawerContent()}
        </StyledDrawer>
      ) : (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderWidth: 0,
            },
          }}
          anchor="left"
          open={open}
          onClose={onClose}
        >
          {getDrawerContent()}
          <LogoutDialog visibility={isLogout} dismissModal={dismissModal} />
        </Drawer>

      )}
      {!open && hoveredItem && (
        <Box
          sx={{
            position: "fixed",
            left: closedDrawerWidth,
            top: getStripTop(),
            height: `${itemHeight}px`,
            width: 200,
            backgroundColor: theme.palette.primary.main,
            zIndex: theme.zIndex.drawer + 1,
            display: "flex",
            alignItems: "center",
            paddingLeft: theme.spacing(2),
            transition: theme.transitions.create(["top", "opacity"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          }}
        >
          <Typography
            color="white"
            sx={{
              fontSize: "18px",
              fontFamily: "Nunito Sans SemiBold",
            }}
          >
            {hoveredItem}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CommonDrawer;
